import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { fmtDate } from "@/lib/api";
import { SectionTitle } from "@/components/common";
import { FileWarning, FileText, ArrowRight } from "lucide-react";

const DOC_TIPI = [
  ["incarico", "Incarico"], ["ape", "APE"], ["planimetria", "Planimetria"], ["visura", "Visura catastale"],
  ["atto", "Atto di provenienza"], ["documento_identita", "Documento identità"], ["privacy", "Privacy"],
  ["successione", "Successione"], ["locazione", "Contratto di locazione"], ["condominiali", "Documenti condominiali"],
  ["titoli_edilizi", "Titoli edilizi"], ["agibilita", "Agibilità / abitabilità"], ["altro", "Altro"],
];
const DOC_LABEL = Object.fromEntries(DOC_TIPI);
const WINDOWS = [[7, "7 giorni"], [15, "15 giorni"], [30, "30 giorni"], [60, "60 giorni"], [90, "90 giorni"]];

const daysLeft = (iso) => { try { return Math.ceil((new Date(iso) - new Date()) / 86400000); } catch { return null; } };

const urgency = (dl) => {
  if (dl == null) return null;
  if (dl <= 7) return { label: "Urgente", cls: "text-red-600 bg-red-100 border-red-300" };
  if (dl <= 15) return { label: "A breve", cls: "text-orange-600 bg-orange-100 border-orange-300" };
  if (dl <= 30) return { label: "Da pianificare", cls: "text-[#9A6A00] bg-[#FFB020]/15 border-[#FFB020]/40" };
  return { label: "In programma", cls: "text-slate-500 bg-slate-100 border-slate-300" };
};

export default function DocumentiScadenza() {
  const [params] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(Number(params.get("days")) || 15);
  const [tipo, setTipo] = useState(params.get("tipo") || "");
  const nav = useNavigate();

  useEffect(() => {
    setLoading(true);
    api.get("/documenti/scadenza", { params: { days } })
      .then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [days]);

  const counts = items.reduce((acc, d) => { acc[d.doc_tipo] = (acc[d.doc_tipo] || 0) + 1; return acc; }, {});
  const shown = (tipo ? items.filter((d) => d.doc_tipo === tipo) : items)
    .slice()
    .sort((a, b) => (daysLeft(a.scadenza) ?? 9e9) - (daysLeft(b.scadenza) ?? 9e9));

  return (
    <div className="fade-up" data-testid="documenti-scadenza-page">
      <SectionTitle sub="I tuoi documenti in scadenza, ordinati dai più urgenti. Filtra per tipo e finestra temporale.">Documenti in scadenza</SectionTitle>

      <div className="flex flex-wrap items-center gap-2 mt-4 mb-3">
        {WINDOWS.map(([v, l]) => (
          <button key={v} data-testid={`win-${v}`} onClick={() => setDays(v)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${days === v ? "bg-[#FC82ED] text-black border-[#FC82ED]" : "bg-white text-[var(--muted)] border-[var(--border)] hover:text-[#111111]"}`}>{l}</button>
        ))}
        <span className="w-px h-5 bg-[var(--border)] mx-1" />
        <select data-testid="doc-tipo-filter" value={tipo} onChange={(e) => setTipo(e.target.value)}
          className="px-3 py-1.5 rounded-lg bg-white border border-[var(--border)] focus:border-[#FC82ED] outline-none text-xs">
          <option value="">Tutti i tipi</option>
          {DOC_TIPI.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>

      {items.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="doc-type-badges">
          <button data-testid="badge-tipo-tutti" onClick={() => setTipo("")}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${tipo === "" ? "bg-[#111111] text-white border-[#111111]" : "bg-white text-[var(--muted)] border-[var(--border)] hover:text-[#111111]"}`}>
            Tutti <span className="font-bold">{items.length}</span>
          </button>
          {DOC_TIPI.filter(([v]) => counts[v]).map(([v, l]) => (
            <button key={v} data-testid={`badge-tipo-${v}`} onClick={() => setTipo(tipo === v ? "" : v)}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${tipo === v ? "bg-[#FC82ED] text-black border-[#FC82ED]" : "bg-white text-[var(--muted)] border-[var(--border)] hover:text-[#111111]"}`}>
              {l} <span className="font-bold">{counts[v]}</span>
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="space-y-2">{[...Array(4)].map((_, i) => <div key={i} className="skeleton h-16 rounded-xl" />)}</div>
      ) : shown.length === 0 ? (
        <div className="card-immo p-12 text-center" data-testid="doc-scadenza-empty">
          <FileWarning className="mx-auto text-[var(--muted)] mb-3" size={32} />
          <p className="text-[var(--muted)] text-sm">Nessun documento in scadenza per i filtri selezionati.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {shown.map((d) => {
            const dl = daysLeft(d.scadenza);
            const urgent = dl != null && dl <= 5;
            const u = urgency(dl);
            const target = d.immobile_id ? `/immobili/${d.immobile_id}` : (d.notizia_id ? `/notizie/${d.notizia_id}` : null);
            return (
              <div key={d.id} data-testid={`doc-scadenza-${d.id}`}
                onClick={() => target && nav(target)}
                className={`card-immo p-4 flex items-center gap-3 ${target ? "cursor-pointer hover:-translate-y-0.5 transition-transform" : ""}`}>
                <FileText size={18} className="text-[#FC82ED] shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] font-bold uppercase bg-[#FC82ED]/15 text-[#111111] px-1.5 py-0.5 rounded">{DOC_LABEL[d.doc_tipo] || "Altro"}</span>
                    {u && <span data-testid={`doc-urgency-${d.id}`} className={`inline-flex items-center text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full border ${u.cls}`}>{u.label}</span>}
                    <span className="text-sm font-medium truncate">{d.titolo || d.original_filename}</span>
                  </div>
                  <div className="text-[11px] text-[var(--muted)] mt-0.5 truncate">{d.original_filename}</div>
                </div>
                <div className={`text-right shrink-0 ${urgent ? "text-red-500" : "text-[var(--muted)]"}`}>
                  <div className="text-sm font-semibold">{fmtDate(d.scadenza)}</div>
                  <div className="text-[11px]">{dl != null ? (dl <= 0 ? "Scaduto" : `tra ${dl} gg`) : ""}</div>
                </div>
                {target && <ArrowRight size={16} className="text-[var(--muted)] shrink-0" />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
